import React from 'react';
import { motion } from 'framer-motion';
const blocks = [{
  id: 'sound',
  heading: 'Sound',
  body: 'We believe sound should be accurate, not exaggerated. Which is why we work with systems designed for studios, not showrooms.',
  micro: 'PMC',
  color: '#6366F1',
  textColor: 'text-[#6366F1]',
  bgClass: 'bg-void',
  gradient: 'radial-gradient(circle at right center, rgba(99,102,241,0.08) 0%, transparent 60%)'
}, {
  id: 'bass',
  heading: 'Bass',
  body: 'Bass should integrate seamlessly into the room — not overpower it.',
  micro: 'REL',
  color: '#EC4899',
  textColor: 'text-[#EC4899]',
  bgClass: 'bg-obsidian',
  gradient: 'radial-gradient(circle at left center, rgba(236,72,153,0.08) 0%, transparent 60%)'
}, {
  id: 'control',
  heading: 'Control',
  body: 'Technology should disappear behind a single, intuitive interface.',
  micro: 'Crestron',
  color: '#F59E0B',
  textColor: 'text-[#F59E0B]',
  bgClass: 'bg-void',
  gradient: 'radial-gradient(circle at right center, rgba(245,158,11,0.08) 0%, transparent 60%)'
}, {
  id: 'experience',
  heading: 'Experience',
  body: 'For select spaces, we design fully immersive environments where sound behaves like architecture.',
  micro: 'Meyer Sound',
  color: '#10B981',
  textColor: 'text-[#10B981]',
  bgClass: 'bg-obsidian',
  gradient: 'radial-gradient(circle at left center, rgba(16,185,129,0.08) 0%, transparent 60%)'
}];
const approaches = [{
  num: '01',
  text: 'Designed before it is priced',
  color: 'text-[#6366F1]'
}, {
  num: '02',
  text: 'Simulated before it is installed',
  color: 'text-[#EC4899]'
}, {
  num: '03',
  text: 'Calibrated after it is built',
  color: 'text-[#F59E0B]'
}];
export function SystemPhilosophy() {
  return <section className="border-t border-graphite" data-id="element-476">
      {/* Main Header */}
      <div className="py-32 bg-void text-center px-6" data-id="element-477">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-display font-serif text-gradient-vibrant max-w-4xl mx-auto leading-tight" data-id="element-478">
          We don't choose products.
          <br data-id="element-479" />
          We choose outcomes.
        </motion.h2>
      </div>

      {/* Philosophy Blocks */}
      {blocks.map((block, idx) => <div key={block.id} className={`relative py-24 px-6 md:px-12 ${block.bgClass} overflow-hidden`} data-id="element-480">
          <div className="absolute inset-0 pointer-events-none" style={{
        background: block.gradient
      }} data-id="element-481" />

          <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-10%'
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl mx-auto flex flex-col md:flex-row md:items-center gap-8 md:gap-16 relative z-10" data-id="element-482">
            <div className="md:w-1/3" data-id="element-483">
              <h3 className={`text-headline font-serif ${block.textColor}`} data-id="element-484">
                {block.heading}
              </h3>
            </div>
            <div className="md:w-2/3" data-id="element-485">
              <p className="text-2xl font-light text-platinum leading-relaxed mb-6" data-id="element-486">
                {block.body}
              </p>
              <p className={`text-sm tracking-[0.2em] uppercase font-medium ${block.textColor} opacity-60`} data-id="element-487">
                {block.micro}
              </p>
            </div>
          </motion.div>
        </div>)}

      {/* Design Approach */}
      <div className="py-32 bg-void px-6 md:px-12 border-t border-graphite" data-id="element-488">
        <div className="max-w-7xl mx-auto" data-id="element-489">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-490">
            <h3 className="text-title font-serif text-platinum" data-id="element-491">
              Our Approach
            </h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-492">
            {approaches.map((app, idx) => <motion.div key={app.num} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2
          }} className="bg-carbon border border-graphite rounded-2xl p-10 relative flex flex-col items-center justify-center text-center min-h-[200px]" data-id="element-493">
                <span className={`absolute top-6 left-6 text-3xl font-serif ${app.color} opacity-50`} data-id="element-494">
                  {app.num}
                </span>
                <p className="text-xl text-platinum font-light mt-4" data-id="element-495">
                  {app.text}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Final Cinematic Line */}
      <div className="py-40 bg-obsidian text-center px-6 border-t border-graphite relative overflow-hidden" data-id="element-496">
        <div className="absolute inset-0 film-grain opacity-30 pointer-events-none" data-id="element-497"></div>
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-20%'
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} className="relative z-10" data-id="element-498">
          <h2 className="text-display font-serif text-platinum mb-2" data-id="element-499">
            You don't see the system.
          </h2>
          <h2 className="text-display font-serif text-gradient-vibrant" data-id="element-500">
            You feel it.
          </h2>
        </motion.div>
      </div>
    </section>;
}